import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ParentContainor from '../../components/base/ParentContainor';
import { ROUTE_ENUM } from '../../routes/RoutesConstant';
import { IMAGE_CONSTANTS } from '../../constants/ImageConstans';
import { TEXT_LABELS } from '../../constants/TextLabelConstants';
import { OTPInput } from '../../components/OtpInput';
import { logger } from '../../utils/logger';
import { authService } from '../../services/authService';
import { attachIdentityIdToPolicyViaAPI, createUserProfile, getUserProfile } from '../../network/network_callbacks';
import { saveUserProfile } from '../../utils/userProfileStorage';
import amplifyConfig from '../../config/amplify';
import './Login.css';

const RESEND_TIME = 3 * 60;

const OtpVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { phoneNumber, email, countryCode, isSignUp } = location.state || {};
  const destination = phoneNumber || email;

  const formatDestination = () => {
    if (email) return email;
    if (!phoneNumber) return TEXT_LABELS.AUTH_OTP_YOUR_ACCOUNT;
    if (countryCode && phoneNumber.startsWith(countryCode)) {
      const national = phoneNumber.slice(countryCode.length);
      return national ? `${countryCode} ${national}` : phoneNumber;
    }
    const match = phoneNumber.match(/^(\+\d{1,4})(\d{4,})$/);
    return match ? `${match[1]} ${match[2]}` : phoneNumber;
  };
  const [otp, setOtp] = useState('');
  const [otpResetKey, setOtpResetKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState(null);
  const [resendTimer, setResendTimer] = useState(RESEND_TIME);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [resendTimer]);

  const handleProceed = async (e) => {
    e?.preventDefault();
    logger.log('Proceeding with OTP verification for ', { phoneNumber, email, otp, isSignUp });
    if (!destination) {
      setError(TEXT_LABELS.AUTH_OTP_MISSING_IDENTIFIER);
      return;
    }
    if (otp.length !== 6) {
      setError(TEXT_LABELS.AUTH_OTP_INCOMPLETE);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      if (isSignUp) {
        const result = await authService.confirmSignUp(phoneNumber, email, otp);
        logger.log('Sign up confirmation result:', result);
        window.location.replace(ROUTE_ENUM.AUTH_LOGIN.routePath);
        return;
      }

      const result = await authService.confirmSignIn(phoneNumber, email, otp);
      logger.log('Sign in confirmation result2:', result);
      if (!result?.isSignedIn) {
        logger.log('OTP verification failed during sign in:', result);
        setError(TEXT_LABELS.AUTH_OTP_INVALID);
        return;
      }

      const userDetails = await authService.getCurrentUserDetails();
      logger.log('User userDetails after sign up:', userDetails);

      const userAuthTokenDetails = await authService.getAuthTokens();
      logger.log('User userAuthTokenDetails after sign up:', userAuthTokenDetails);

      const userProfileDetail = await getUserProfile();
      logger.log('Fetched user profile detail:', userProfileDetail?.data?.data?.userId);

      if (!userProfileDetail?.data?.data?.userId) {
        logger.log('Creating user profile as it does not exist.');

        let cleanPhone = phoneNumber;
        if (cleanPhone?.startsWith('+91')) {
          cleanPhone = cleanPhone?.slice(3);
        }
        cleanPhone = cleanPhone?.replace(/\D/g, '');
        if (cleanPhone?.length > 10) {
          cleanPhone = cleanPhone?.slice(-10);
        }
        const userProfileData = {
          userName: `user_${cleanPhone || 'email'}_${Math.floor(Math.random() * 10000)}_${Date.now()}`,
          profilePicture: 'https://avatar.iran.liara.run/public/34',
          identityId: `${amplifyConfig.Auth.Cognito.region}:${userDetails?.userId}`,
          age: `27`,
          phoneNumber: cleanPhone,
        };

        const resultUserProfile = await createUserProfile(userProfileData);
        const IotPolicyResult = await attachIdentityIdToPolicyViaAPI(userAuthTokenDetails?.identityId);

        logger.log('User profile creation result:', resultUserProfile);
        logger.log('User IoT policy attachment result:', IotPolicyResult);

        const createdProfileDetail = await getUserProfile();
        saveUserProfile(createdProfileDetail?.data?.data || resultUserProfile?.data?.data);
      } else {
        logger.log('User profile already exists. No need to create.');
        saveUserProfile(userProfileDetail?.data?.data);
      }
      window.location.replace(ROUTE_ENUM.SELECT_GAME.routePath);
    } catch (err) {
      setError(err.message || TEXT_LABELS.AUTH_OTP_INVALID);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsResending(true);
    setError(null);
    try {
      if (isSignUp) {
        if (email) {
          await authService.resendSignUpCode('', email);
        } else if (phoneNumber) {
          await authService.resendSignUpCode(phoneNumber, '');
        } else {
          throw new Error(TEXT_LABELS.AUTH_OTP_MISSING_IDENTIFIER);
        }
      } else if (email) {
        await authService.signInEmail(email);
      } else if (phoneNumber) {
        await authService.signIn(phoneNumber);
      } else {
        throw new Error(TEXT_LABELS.AUTH_OTP_MISSING_IDENTIFIER);
      }
      setOtp('');
      setOtpResetKey((prev) => prev + 1);
      setResendTimer(RESEND_TIME);
    } catch (err) {
      setError(err.message || TEXT_LABELS.AUTH_OTP_RESEND_FAILED);
    } finally {
      setIsResending(false);
    }
  };

  const timerLabel = `${String(Math.floor(resendTimer / 60)).padStart(2, '0')}:${String(resendTimer % 60).padStart(2, '0')}`;

  return (
    <ParentContainor
      route={ROUTE_ENUM.AUTH_LOGIN}
      showCopyRightText
      disablePadding
      isShowHeader={false}
    >
      <div className="rap-auth">
        <div className="rap-auth__card">
          <div className="rap-auth__brand">
            <img
              className="rap-auth__logo"
              src={IMAGE_CONSTANTS.V2_REVAMPED_RAPCHIK_LOGO}
              alt="Rapchik"
              width={187}
              height={166}
            />
            <h1 className="rap-auth__headline">
              <span className="rap-auth__headline-regular">{TEXT_LABELS.AUTH_LOGIN_HEADLINE_KHELO}</span>
              {` ${TEXT_LABELS.AUTH_LOGIN_HEADLINE_DIL_SE} `}
              <span className="rap-auth__headline-regular">{TEXT_LABELS.AUTH_LOGIN_HEADLINE_JEETO}</span>
              {` ${TEXT_LABELS.AUTH_LOGIN_HEADLINE_DIMAAG_SE}`}
            </h1>
          </div>

          <img
            className="rap-auth__divider"
            src={IMAGE_CONSTANTS.V2_REVAMPED_LOGIN_DIVIDER}
            alt=""
          />

          <div className="rap-auth__otp-copy">
            <p className="rap-auth__otp-copy-line">{TEXT_LABELS.AUTH_INPUT_OTP_SUBTITLE}</p>
            <p className="rap-auth__otp-copy-line">
              <strong>{formatDestination()}</strong>
              {' '}
              <button
                type="button"
                className="rap-auth__change"
                onClick={() => navigate(ROUTE_ENUM.AUTH_LOGIN.routePath)}
              >
                {TEXT_LABELS.AUTH_OTP_CHANGE}
              </button>
            </p>
          </div>

          <form className="rap-auth__form" onSubmit={handleProceed}>
            <OTPInput
              key={otpResetKey}
              otpBoxCount={6}
              className="rap-auth__otp"
              inputClassName="rap-auth__otp-box"
              onChange={(value) => {
                setOtp(value);
                setError(null);
              }}
            />

            {error ? <div className="rap-auth__error">{error}</div> : null}

            {resendTimer > 0 ? (
              <p className="rap-auth__otp-status">
                <span className="rap-auth__otp-sent">{TEXT_LABELS.AUTH_OTP_SENT}</span>
                <span className="rap-auth__otp-timer">{timerLabel}</span>
              </p>
            ) : (
              <button
                type="button"
                className="rap-auth__resend"
                disabled={isLoading || isResending || !destination}
                onClick={handleResendOTP}
              >
                {isResending ? TEXT_LABELS.AUTH_ACTION_BUTTON_SEND_OTP : TEXT_LABELS.AUTH_ACTION_BUTTON_RESEND_OTP}
              </button>
            )}

            <button
              type="submit"
              className="rap-auth__submit"
              disabled={isLoading || !destination}
            >
              <span className="rap-auth__submit-face">
                {isLoading ? TEXT_LABELS.AUTH_ACTION_BUTTON_VERIFIY_OTP : TEXT_LABELS.AUTH_ACTION_BUTTON_SUBMIT_OTP}
              </span>
            </button>
          </form>
        </div>
      </div>
    </ParentContainor>
  );
};

export default OtpVerification;
